/**
 * HomeTaskMetrics.jsx — Home dashboard interactive Metric Grid (Row 2 of
 * the Ultra-Wide Command Center).
 *
 * Six primary task-status cards (To do / In progress / Delayed / Not
 * assigned / Done / In review), Delayed shown in red. Clicking a card
 * drills the TaskHubWidget list below down to that status — see
 * `statusFilter`/`onStatusFilterChange`. Person/Project/Business-unit
 * scoping comes from the Master Filter Bar above (Phase 120) — this
 * component no longer owns its own dropdowns.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { isDelayed } from "@/lib/dailyTaskStatus";

const CARDS = [
  { key: "planned", label: "To do" },
  { key: "in_progress", label: "In progress" },
  { key: "delayed", label: "Delayed" },
  { key: "unassigned", label: "Not assigned" },
  { key: "done", label: "Done" },
  { key: "review", label: "In review" },
];

export default function HomeTaskMetrics({ statusFilter, onStatusFilterChange, personId, projectId, businessUnit }) {
  const [rows, setRows] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    const loadRows = () => api.get("/daily-tasks?assignee_id=all").then(({ data }) => setRows(data || [])).catch(() => setRows([]));
    loadRows();
    api.get("/daily-tasks/projects").then(({ data }) => setProjects(data || [])).catch(() => setProjects([]));
    // Refresh the counts whenever the Task Hub adds, edits or deletes a task.
    // Debounced so a burst of edits (or the widget's own reload firing on the
    // same event) coalesces into one refetch instead of a request storm.
    let t;
    const onChanged = () => { clearTimeout(t); t = setTimeout(loadRows, 300); };
    window.addEventListener("daily-tasks:changed", onChanged);
    return () => { clearTimeout(t); window.removeEventListener("daily-tasks:changed", onChanged); };
  }, []);

  const buByPid = useMemo(() => {
    const m = {};
    for (const p of projects) m[p.id] = p.business_unit;
    return m;
  }, [projects]);

  // Project + Business-unit scoping applies team-wide, before any person filter.
  const scopedAll = useMemo(() => {
    let out = rows || [];
    if (projectId) out = out.filter((r) => r.project_id === projectId);
    if (businessUnit) out = out.filter((r) => buByPid[r.project_id] === businessUnit);
    return out;
  }, [rows, projectId, businessUnit, buByPid]);

  // Team Member (Master Filter Bar) further narrows to one assignee.
  const scopedRows = useMemo(() => (personId ? scopedAll.filter((r) => r.assignee_id === personId) : scopedAll), [scopedAll, personId]);

  const counts = useMemo(() => ({
    planned: scopedRows.filter((r) => r.status === "planned").length,
    in_progress: scopedRows.filter((r) => r.status === "in_progress").length,
    review: scopedRows.filter((r) => r.status === "review").length,
    done: scopedRows.filter((r) => r.status === "done").length,
    // "Not assigned" is always the team-wide (project/BU-scoped) count —
    // a person filter can never match an unassigned task.
    unassigned: scopedAll.filter((r) => !r.assignee_id).length,
    delayed: scopedRows.filter(isDelayed).length,
  }), [scopedRows, scopedAll]);

  if (rows === null) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {CARDS.map((c) => <div key={c.key} className="card-flat p-4 h-[76px] animate-pulse bg-slate-50" />)}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="home-task-metrics">
      {CARDS.map((c) => {
        const isActive = statusFilter === c.key;
        return (
          <button
            key={c.key}
            type="button"
            onClick={() => onStatusFilterChange(isActive ? null : c.key)}
            className={`card-flat p-4 text-left transition-all ${isActive ? "border-[#F54800] ring-1 ring-[#F54800]/30 bg-[#FFF4EE]" : "hover:border-slate-300"}`}
            data-testid={`home-metric-card-${c.key}`}
          >
            <div className={`label-overline ${isActive ? "text-[#F54800]" : ""}`}>{c.label}</div>
            <div className={`text-2xl font-display font-bold mt-1 ${isActive ? "text-[#F54800]" : c.key === "delayed" && counts[c.key] > 0 ? "text-red-600" : "text-slate-900"}`} data-testid={`home-metric-value-${c.key}`}>
              {counts[c.key]}
            </div>
          </button>
        );
      })}
    </div>
  );
}
